import { useCallback, useState } from 'react';
import type { HealthStatus } from '../types';
import { createLogger } from '../utils/logger';
import { apiFetch } from '../utils/api';

const log = createLogger('SystemStatusHeader');

interface SystemStatusHeaderProps {
  health: HealthStatus | null;
}

interface CounterPillProps {
  label: string;
  value: number;
  tone: 'amber' | 'slate' | 'green' | 'red';
  trailing?: React.ReactNode;
}

const TONE_PILL: Record<CounterPillProps['tone'], string> = {
  amber: 'bg-amber-50 text-amber-600',
  slate: 'bg-slate-100 text-slate-600',
  green: 'bg-green-50 text-green-600',
  red: 'bg-red-50 text-red-600',
};

function CounterPill({ label, value, tone, trailing }: CounterPillProps) {
  return (
    <div className={`relative group inline-flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-1.5 text-sm shadow-lg hover:shadow-xl transition-all duration-200 ${TONE_PILL[tone]}`}>
      <span className="font-semibold tabular-nums">{value}</span>
      <span className="text-xs uppercase tracking-wide opacity-80">{label}</span>
      {trailing}
    </div>
  );
}

export function SystemStatusHeader({ health }: SystemStatusHeaderProps) {
  const [cleaningUp, setCleaningUp] = useState(false);
  const [cleanupResult, setCleanupResult] = useState<{ cleaned: number } | null>(null);

  const cleanupOrphanedJobs = useCallback(async () => {
    setCleaningUp(true);
    setCleanupResult(null);
    try {
      const response = await apiFetch('/api/jobs/cleanup', { method: 'POST' });
      if (response.ok) {
        const data = await response.json();
        log.info(`Cleanup completed: ${data.cleaned} orphaned jobs cleaned`);
        setCleanupResult({ cleaned: data.cleaned });
        setTimeout(() => setCleanupResult(null), 3000);
      } else {
        log.error(`Cleanup failed: ${response.status} ${response.statusText}`);
      }
    } catch (error) {
      log.error('Failed to cleanup orphaned jobs:', error);
    } finally {
      setCleaningUp(false);
    }
  }, []);

  return (
    <div className="flex flex-col items-start gap-2">
      <div className="flex items-center gap-2 flex-nowrap justify-start">
        {health && (
          <div className="flex items-center gap-2">
            <CounterPill label="Active" value={health.active_jobs} tone="amber" />
            <CounterPill
              label="Pending"
              value={health.pending_jobs}
              tone="slate"
              trailing={
                (health.pending_jobs > 0 || health.active_jobs > 0) && (
                  <button
                    onClick={cleanupOrphanedJobs}
                    disabled={cleaningUp}
                    className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-red-500 text-white text-[10px] opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600 flex items-center justify-center shadow-sm"
                    title="Clear stuck jobs"
                  >
                    {cleaningUp ? '·' : '×'}
                  </button>
                )
              }
            />
            <CounterPill label="Complete" value={health.completed_jobs} tone="green" />
            <CounterPill label="Failed" value={health.failed_jobs} tone="red" />
          </div>
        )}
      </div>

      {cleanupResult && (
        <div className="px-3 py-1.5 bg-green-50 text-green-600 rounded-full text-xs flex items-center gap-2">
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          Cleaned up {cleanupResult.cleaned} orphaned job{cleanupResult.cleaned !== 1 ? 's' : ''}
        </div>
      )}
    </div>
  );
}
